import { Texture } from "three";
import { C_BORDO_SCURO } from "./constants";

/**
 * Tratti con cui si disegnano sezioni, rail e contorni sopra una finitura.
 * Sono volutamente vicini al nero e al bianco: sui campioni a texture (woodstyle,
 * HPL, pietravera) un grigio medio si confonde con la venatura e le sezioni
 * spariscono.
 */
export const C_TRATTO_SCURO  = "#1f1f1f";
export const C_TRATTO_CHIARO = "#f2f2f2";

// La luminanza di un campione non cambia mai: si calcola una volta per immagine.
const cacheLuminanza = new Map<string, number>();

/**
 * Luminanza media (0..1) del campione di finitura, letta ridisegnandolo su un
 * canvas minuscolo. Restituisce undefined se l'immagine non e' ancora decodificata
 * o se il canvas e' "tainted" (campione cross-origin servito senza header CORS).
 */
export function luminanzaTexture(txt: Texture | undefined): number | undefined {
  const img = txt?.image as (HTMLImageElement | ImageBitmap | undefined);
  if (!img) return undefined;

  const larghezza = (img as HTMLImageElement).naturalWidth || img.width;
  const altezza   = (img as HTMLImageElement).naturalHeight || img.height;
  if (!larghezza || !altezza) return undefined;

  const chiave = (img as HTMLImageElement).src || txt!.uuid;
  const inCache = cacheLuminanza.get(chiave);
  if (inCache !== undefined) return inCache;

  try {
    const LATO = 32;   // il campione viene ridotto: serve la media, non il dettaglio
    const canvas = document.createElement("canvas");
    canvas.width  = LATO;
    canvas.height = LATO;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return undefined;
    ctx.drawImage(img as CanvasImageSource, 0, 0, LATO, LATO);

    const { data } = ctx.getImageData(0, 0, LATO, LATO);
    let somma = 0;
    for (let i = 0; i < data.length; i += 4) {
      somma += (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
    }
    const media = somma / (data.length / 4);

    cacheLuminanza.set(chiave, media);
    return media;
  } catch {
    return undefined;
  }
}

/**
 * Colore del tratto da usare sopra una finitura a texture: chiaro sui campioni
 * scuri, scuro su quelli chiari. Con luminanza non calcolabile resta il fallback.
 */
export function coloreTrattoSuTexture(
  txt: Texture | undefined,
  fallback: string = C_BORDO_SCURO,
): string {
  const luminanza = luminanzaTexture(txt);
  if (luminanza === undefined) return fallback;
  return luminanza > 0.5 ? C_TRATTO_SCURO : C_TRATTO_CHIARO;
}
